import { useCallback, useMemo, useEffect } from 'react';
import { useAtom } from 'jotai';
import { Permissions } from '../typings/user';
import getWabaLevelUserPermissions from '../requests/get-waba-level-permissions';
import { WabaLevelUserPermissionsResponse } from '../typings/waba-level-permission';
import useQuery from './useQuery';
import { wabaLevelPermissionsAtom } from '../states/waba-level-permissions.atom';

export const useWabaLevelPermission = () => {
  const [wabaPermissionsState] = useAtom(wabaLevelPermissionsAtom);
  const hasCachedData = Boolean(wabaPermissionsState?.data);
  const isLoading = Boolean(wabaPermissionsState?.loading);

  const {
    requestData: { data, loading, error },
    fetchData,
  } = useQuery<WabaLevelUserPermissionsResponse>({
    queryFunction: getWabaLevelUserPermissions,
    requestAtom: wabaLevelPermissionsAtom,
    cacheData: true,
    autoFetch: false,
    getCachedData: true,
  });

  // Only fetch data if we don't have cached data and no other instance is already loading
  useEffect(() => {
    if (!hasCachedData && !isLoading) {
      fetchData();
    }
  }, [hasCachedData, isLoading, fetchData]);

  const hasPermission = useCallback(
    (
      integrationId: string,
      permissionKey: Permissions
    ): {
      hasAccess: boolean;
      loading: boolean;
      error: Error | null;
    } => {
      if (loading) return { hasAccess: false, loading: true, error: null };
      if (error) return { hasAccess: false, loading: false, error };
      if (!data) return { hasAccess: false, loading: false, error: null };

      const record = data.access[integrationId];
      if (!record) return { hasAccess: false, loading: false, error: null };

      return {
        hasAccess: record.permissions?.includes(permissionKey) ?? false,
        loading: false,
        error: null,
      };
    },
    [data, loading, error]
  );

  const getWabasWithPermission = useCallback(
    (
      permissionKey: Permissions
    ): {
      wabas: { wabaId: string; roles: string[] }[];
      loading: boolean;
      error: Error | null;
    } => {
      if (loading) return { wabas: [], loading: true, error: null };
      if (error) return { wabas: [], loading: false, error };
      if (!data || !data.access) return { wabas: [], loading: false, error: null };

      const wabas = Object.entries(data.access)
        .filter(([_, record]) => record.permissions?.includes(permissionKey))
        .map(([wabaId, record]) => ({
          wabaId,
          roles: record.roles,
        }));

      return {
        wabas,
        loading: false,
        error: null,
      };
    },
    [data, loading, error]
  );

  const value = useMemo(
    () => ({ hasPermission, getWabasWithPermission, data, loading, error }),
    [hasPermission, getWabasWithPermission, loading, error]
  );

  return value;
};

export default useWabaLevelPermission;
